/**
 * Bars (ui-ux §19.9; art §9.3): the swallowtail HP ribbon with the R3 loss chip, the XP line, the «Пыл» bar with cost
 * ticks, generic progress and pip rows. Owner: UI/Visual (F3).
 */
import { formatNumberRu } from '../../shared';
import { h } from '../dom';

export interface RibbonHandle { el: HTMLElement; set(value: number): void; readonly value: number }

/**
 * The HP ribbon. `set(v)` on damage: 2-frame lead-white flash → the fill drops at once → the lost segment stays as a
 * gold chip for 500 ms → drains over 300 ms (CSS transition delay). Heal: chip = fill, then the fill grows.
 * Below 25 % the wrapper pulses a vermilion edge (static under reduced motion).
 */
export function hpRibbon(o: { side: 'player' | 'foe'; max: number; value: number; label: string }): RibbonHandle {
  let value = o.value;
  const fill = h('i', { class: 'mc-ribbon__fill' });
  const chip = h('i', { class: 'mc-ribbon__chip' });
  const num = h('span', { class: 'mc-ribbon__value', attrs: { 'aria-hidden': 'true' } });
  const rib = h('div', {
    class: `mc-ribbon mc-ribbon--${o.side}`,
    attrs: { role: 'progressbar', 'aria-label': o.label, 'aria-valuemin': 0, 'aria-valuemax': o.max, 'aria-valuenow': value },
  }, h('span', { class: 'mc-ribbon__track' }, chip, fill), num);
  const wrap = h('div', { class: 'mc-ribbon-wrap' }, rib);
  const write = (v: number, g: number): void => {
    rib.style.setProperty('--v', String(v / o.max));
    rib.style.setProperty('--g', String(g / o.max));
    num.textContent = o.side === 'player' ? `${formatNumberRu(Math.round(value))}/${formatNumberRu(o.max)}` : `${formatNumberRu(Math.round(value))}/${formatNumberRu(o.max)}`;
    rib.setAttribute('aria-valuenow', String(Math.round(value)));
    rib.setAttribute('aria-valuetext', `${Math.round(value)} из ${o.max}`);
    wrap.classList.toggle('is-low', value / o.max < 0.25 && value > 0);
  };
  write(value, value);
  return {
    el: wrap,
    get value() { return value; },
    set(next: number) {
      const prev = value;
      value = Math.max(0, Math.min(o.max, next));
      if (value < prev) {
        rib.classList.remove('is-hit'); void rib.offsetWidth; rib.classList.add('is-hit');
        // chip holds the old value (no transition on the way up), then drains after its 500 ms delay
        chip.style.transition = 'none';
        rib.style.setProperty('--g', String(prev / o.max));
        void chip.offsetWidth;
        chip.style.transition = '';
        rib.style.setProperty('--v', String(value / o.max));
        requestAnimationFrame(() => write(value, value));
      } else {
        write(value, value);
      }
    },
  };
}

/** The XP «gilded rubric line» (6 px, gold tile fill, lozenge end) inside a 44 px hit area. */
export function xpLine(v: number, label: string): HTMLDivElement {
  return h('div', { class: 'mc-xpline-hit' },
    h('div', { class: 'mc-xpline', attrs: { role: 'progressbar', 'aria-label': label, 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-valuenow': Math.round(v * 100) }, style: { '--v': String(v) } },
      h('i', { class: 'mc-xpline__fill' })));
}

/** The battle «Пыл» bar: 14 px gold fill, a 2 px ink tick at each equipped prayer's cost, the value at the right. */
export function fervorBar(o: { value: number; max: number; costs: number[]; label: string; caption: string }): HTMLDivElement {
  return h('div', { class: 'mc-fervor', attrs: { role: 'meter', 'aria-label': o.label, 'aria-valuemin': 0, 'aria-valuemax': o.max, 'aria-valuenow': o.value } },
    h('span', { class: 'mc-fervor__label', attrs: { 'aria-hidden': 'true' } }, o.caption),
    h('span', { class: 'mc-fervor__track', style: { '--v': String(o.value / o.max) } },
      h('i', { class: 'mc-fervor__fill' }),
      o.costs.map((c) => h('i', { class: 'mc-fervor__tick', attrs: { 'data-cost': c }, style: { '--at': String(c / o.max) } }))),
    h('span', { class: 'mc-fervor__value num', attrs: { 'aria-hidden': 'true' } }, String(o.value)));
}

/** A generic progress bar (`--_fill` colour token). */
export function progressBar(v: number, o: { label: string; fill?: string }): HTMLDivElement {
  return h('div', { class: 'mc-bar', attrs: { role: 'progressbar', 'aria-label': o.label, 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-valuenow': Math.round(v * 100) }, style: { '--v': String(v), ...(o.fill ? { '--_fill': o.fill } : {}) } },
    h('i', { class: 'mc-bar__fill' }));
}

/** A row of pips (candles, enemy Пыл, Signature): `on` of `n` filled. */
export function pips(n: number, on: number, o: { label: string; lozenge?: boolean }): HTMLSpanElement {
  return h('span', { class: 'mc-pips', attrs: { role: 'img', 'aria-label': o.label } },
    Array.from({ length: n }, (_, i) => h('i', { class: `mc-pip${o.lozenge ? ' mc-pip--lozenge' : ''}`, attrs: { 'data-on': i < on } })));
}
